import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { PlotListItem } from '@riverpark/types'

function plotStatusBadge(status: PlotListItem['status']) {
  if (status === 'occupied') return <Badge variant="paid">Occupied</Badge>
  if (status === 'reserved') return <Badge variant="pending">Reserved</Badge>
  return <Badge variant="draft">Available</Badge>
}

function locationLabel(plot: PlotListItem): string {
  const parts = [plot.courtOrStreet, plot.section].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

interface PlotsTableProps {
  plots: PlotListItem[]
  isLoading?: boolean
  plotsBasePath?: string
  membersBasePath?: string
  emptyMessage?: string
}

export function PlotsTable({
  plots,
  isLoading,
  plotsBasePath = '/admin/plots',
  membersBasePath = '/admin/members',
  emptyMessage = 'No plots found. Import the plot register or add a plot manually.',
}: PlotsTableProps) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] border-collapse font-sans text-sm">
          <thead>
            <tr className="border-b border-border-2 bg-cream-2/50">
              <th className="px-6 py-3 text-left font-medium text-muted-foreground">Plot No.</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Location</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Section</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Owner</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Development</th>
              <th className="px-6 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-border-2">
                  <td colSpan={8} className="px-6 py-4">
                    <div className="h-10 animate-pulse rounded bg-cream-2" />
                  </td>
                </tr>
              ))}
            {!isLoading && plots.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-muted-foreground">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {!isLoading &&
              plots.map((plot) => (
                <tr
                  key={plot.id}
                  className="border-b border-border-2 transition-colors last:border-b-0 hover:bg-cream-2/30"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-ink">{plot.newNumber}</div>
                    {plot.oldNumber !== plot.newNumber && (
                      <div className="text-xs text-muted-foreground">Legacy: {plot.oldNumber}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">{locationLabel(plot)}</td>
                  <td className="px-4 py-4 text-muted-foreground">{plot.section ?? '—'}</td>
                  <td className="px-4 py-4">{plotStatusBadge(plot.status)}</td>
                  <td className="px-4 py-4">
                    {plot.currentOwner ? (
                      <Link
                        to={`${membersBasePath}/${plot.currentOwner.memberId}`}
                        className="font-medium text-ink hover:text-primary"
                      >
                        {plot.currentOwner.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {plot.plotType === 'special_use' ? (
                      <Badge variant="draft">Special use</Badge>
                    ) : (
                      <span className="text-muted-foreground">Residential</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <Badge variant={plot.developmentStatus === 'developer' ? 'paid' : 'draft'}>
                      {plot.developmentStatus === 'developer' ? 'Developer' : 'Owner'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link to={`${plotsBasePath}/${plot.id}`}>
                      <Button variant="secondary" size="sm" type="button">
                        View
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
